import {
  useMutation,
  useQueryClient,
  type QueryClient
} from '@tanstack/react-query';
import { toast } from 'sonner';
import { useTranslations } from 'next-intl';
import { photoScanningApi } from './photo-scanning.api';
import { photoScanningKeys } from './photo-scanning.queries';
import { apiClient } from '@/lib/api/client';
import { API_ENDPOINTS } from '@/lib/api/config';
import type { ApiResponse } from '@/lib/api/types';
import type { BatchPageRecord, QrBboxData, ScanBatch } from '../types';

type CachedBatchDetail = ApiResponse<ScanBatch & { pages: BatchPageRecord[] }>;

/**
 * Optimistically rewrite the cached batch-detail entry so the UI reflects a
 * scan/delete the moment the mutation resolves. Counters are recomputed from
 * the new `pages` array — only `item_type === 'page'` rows count, matching
 * the backend `onlyPages()` scope.
 */
function updateBatchPagesInCache(
  queryClient: QueryClient,
  batchId: number,
  updater: (pages: BatchPageRecord[]) => BatchPageRecord[]
) {
  queryClient.setQueryData<CachedBatchDetail>(
    photoScanningKeys.detail(batchId),
    (old) => {
      if (!old?.data) return old;
      const nextPages = updater(old.data.pages ?? []);
      const actual = nextPages.filter((p) => p.item_type === 'page');
      return {
        ...old,
        data: {
          ...old.data,
          pages: nextPages,
          scanned_pages: actual.length,
          success_count: actual.filter((p) => p.status === 'success').length,
          failed_count: actual.filter((p) => p.status === 'failed').length,
          fragile_count: actual.filter((p) => p.status === 'fragile').length
        }
      };
    }
  );
}

export function useStartBatchMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('photoScanning');

  return useMutation({
    mutationFn: (id: number) => photoScanningApi.startBatch(id),
    onSuccess: (_data, id) => {
      toast.success(t('startBatchSuccess'));
      queryClient.invalidateQueries({ queryKey: photoScanningKeys.lists() });
      queryClient.invalidateQueries({ queryKey: photoScanningKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: photoScanningKeys.metrics() });
    }
  });
}

export function useScanPageMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ batchId, qrCode }: { batchId: number; qrCode?: string }) =>
      photoScanningApi.scanPage(batchId, { qr_code: qrCode }),
    onSuccess: (response, { batchId }) => {
      const page = response?.data;
      if (page) {
        // Merge by id so the same backend row coming back after a barcode
        // match (replace) doesn't end up double-listed in the cache.
        updateBatchPagesInCache(queryClient, batchId, (pages) => {
          const idx = pages.findIndex((p) => p.id === page.id);
          if (idx !== -1) {
            const next = pages.slice();
            next[idx] = page;
            return next;
          }
          return [...pages, page];
        });
      }
      queryClient.invalidateQueries({ queryKey: photoScanningKeys.metrics() });
    }
  });
}

export function useCompleteBatchMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('photoScanning');

  return useMutation({
    mutationFn: (id: number) => photoScanningApi.completeBatch(id),
    onSuccess: (_data, id) => {
      toast.success(t('completeBatchSuccess'));
      queryClient.invalidateQueries({ queryKey: photoScanningKeys.lists() });
      queryClient.invalidateQueries({ queryKey: photoScanningKeys.detail(id) });
      queryClient.invalidateQueries({ queryKey: photoScanningKeys.metrics() });
    }
  });
}

export function useScanFromCameraMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('photoScanning');

  return useMutation({
    mutationFn: ({
      batchId,
      image,
      expectedSequence,
      qrCode,
      qrBbox,
      onProgress
    }: {
      batchId: number;
      image: Blob;
      expectedSequence?: number;
      qrCode?: string | null;
      qrBbox?: QrBboxData | null;
      onProgress?: (progress: number) => void;
    }) =>
      photoScanningApi.scanPageFromCamera(
        batchId,
        image,
        {
          expectedSequence,
          qrCode,
          qrBbox
        },
        onProgress
      ),
    onSuccess: (response, { batchId }) => {
      const page = response?.data;
      if (page?.qr_status === 'mismatched') {
        toast.warning(
          t('pageMismatched', { scanned: page.barcode ?? page.page_number })
        );
      } else if (page?.qr_status === 'unverified') {
        toast.info(t('pageUnverified', { page: page.page_number }));
      } else if (page?.replaced) {
        toast.info(t('pageReplaced', { page: page.page_number }));
      }
      if (page) {
        updateBatchPagesInCache(queryClient, batchId, (pages) => {
          if (page.replaced) {
            const idx = pages.findIndex((p) => p.id === page.id);
            if (idx !== -1) {
              const next = pages.slice();
              next[idx] = page;
              return next;
            }
          }
          return [...pages, page];
        });
      }
      queryClient.invalidateQueries({ queryKey: photoScanningKeys.metrics() });
    }
  });
}

export function useScanFromImagesMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('photoScanning');

  return useMutation({
    mutationFn: ({
      batchId,
      images,
      qrCodes,
      qrBboxes,
      onProgress
    }: {
      batchId: number;
      images: Blob[];
      qrCodes?: Array<string | null>;
      qrBboxes?: Array<QrBboxData | null>;
      onProgress?: (progress: number) => void;
    }) =>
      photoScanningApi.scanPagesFromImages(
        batchId,
        images,
        qrCodes,
        qrBboxes,
        onProgress
      ),
    onSuccess: (response, { batchId, images }) => {
      toast.success(t('imagesUploadedSuccess', { count: images.length }));
      const newPages = response?.data;
      if (Array.isArray(newPages) && newPages.length > 0) {
        // Merge by id rather than always appending — a matched QR can
        // replace an existing slot in place (backend returns the existing
        // row with new bytes), so appending would double-count and leave
        // the counters out of sync with the grid.
        updateBatchPagesInCache(queryClient, batchId, (pages) => {
          const updatesById = new Map(newPages.map((p) => [p.id, p]));
          const existingIds = new Set(pages.map((p) => p.id));
          const merged = pages.map((p) => updatesById.get(p.id) ?? p);
          for (const np of newPages) {
            if (!existingIds.has(np.id)) merged.push(np);
          }
          return merged;
        });
      }
      queryClient.invalidateQueries({ queryKey: photoScanningKeys.metrics() });
    }
  });
}

export function useRotatePageMutation() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      batchId,
      pageId,
      degrees
    }: {
      batchId: number;
      pageId: number;
      degrees: number;
    }) => photoScanningApi.rotatePage(batchId, pageId, degrees),
    onSuccess: (_data, { batchId }) => {
      queryClient.invalidateQueries({
        queryKey: photoScanningKeys.detail(batchId)
      });
    }
  });
}

export function useBulkDeleteBatchesMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('photoScanning');

  return useMutation({
    mutationFn: photoScanningApi.bulkDelete,
    onSuccess: () => {
      toast.success(t('bulkDeleteSuccess'));
      queryClient.invalidateQueries({ queryKey: photoScanningKeys.lists() });
      queryClient.invalidateQueries({ queryKey: photoScanningKeys.metrics() });
    }
  });
}

export function useEnhancePageMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('photoScanning');

  return useMutation({
    mutationFn: ({
      batchId,
      pageId,
      image,
      onProgress
    }: {
      batchId: number;
      pageId: number;
      image: Blob;
      onProgress?: (progress: number) => void;
    }) => photoScanningApi.enhancePage(batchId, pageId, image, onProgress),
    onSuccess: (_data, { batchId }) => {
      toast.success(t('replaceImageSuccess'));
      queryClient.invalidateQueries({
        queryKey: photoScanningKeys.detail(batchId)
      });
    }
  });
}

export function useDeletePageMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('photoScanning');

  return useMutation({
    mutationFn: ({ batchId, pageId }: { batchId: number; pageId: number }) =>
      photoScanningApi.deletePage(batchId, pageId),
    onSuccess: (_data, { batchId, pageId }) => {
      toast.success(t('deletePageSuccess'));
      updateBatchPagesInCache(queryClient, batchId, (pages) =>
        pages.filter((p) => p.id !== pageId)
      );
      queryClient.invalidateQueries({ queryKey: photoScanningKeys.metrics() });
    }
  });
}

export function useReorderPagesMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('ocr');

  return useMutation({
    mutationFn: ({
      batchId,
      pageIds
    }: {
      batchId: number;
      pageIds: number[];
    }) => photoScanningApi.reorderPages(batchId, pageIds),
    onSuccess: (_data, { batchId }) => {
      toast.success(t('rearrangePagesSuccess'));
      queryClient.invalidateQueries({
        queryKey: photoScanningKeys.detail(batchId)
      });
    }
  });
}

export function useRefuseBatchMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('repairs');

  return useMutation({
    mutationFn: ({
      registrationId,
      ...data
    }: {
      registrationId: number;
      repair_destination_id: number;
      repair_reason_id: number;
      notes?: string;
    }) => photoScanningApi.refuse(registrationId, data),
    onSuccess: () => {
      toast.success(t('transferSuccess'));
      queryClient.invalidateQueries({ queryKey: photoScanningKeys.lists() });
      queryClient.invalidateQueries({ queryKey: photoScanningKeys.metrics() });
    }
  });
}

export function useTransferToQAMutation() {
  const queryClient = useQueryClient();
  const t = useTranslations('photoScanning');

  return useMutation({
    mutationFn: (id: number) =>
      apiClient.post(API_ENDPOINTS.PHOTO_SCANNING.TRANSFER_TO_QA(id), {}),
    onSuccess: (_data, id) => {
      toast.success(t('transferToQaSuccess'));
      queryClient.invalidateQueries({ queryKey: photoScanningKeys.lists() });
      queryClient.invalidateQueries({ queryKey: photoScanningKeys.detail(id) });
    }
  });
}
